// Followup Suggestions Component - Shows follow-up questions based on query response

import React from 'react';
import { 
  ArrowRight, 
  Sparkles, 
  BarChart3, 
  LineChart, 
  PieChart, 
  TrendingUp, 
  GitCompare, 
  Search,
  Zap,
  Target
} from 'lucide-react';
import { COLORS } from '../../constants/theme';

// Helper function to detect suggestion type and return appropriate icon
const getSuggestionIcon = (suggestion) => {
  const lower = suggestion.toLowerCase();
  
  if (lower.includes('chart') || lower.includes('graph') || lower.includes('visualize') || lower.includes('plot')) {
    return { icon: BarChart3, color: COLORS.chart.blue, label: 'Visualize' };
  }
  if (lower.includes('trend') || lower.includes('over time') || lower.includes('timeline')) {
    return { icon: LineChart, color: COLORS.chart.green, label: 'Trend' };
  }
  if (lower.includes('compare') || lower.includes('vs') || lower.includes('versus') || lower.includes('difference')) {
    return { icon: GitCompare, color: COLORS.chart.purple, label: 'Compare' };
  }
  if (lower.includes('pie') || lower.includes('distribution') || lower.includes('breakdown') || lower.includes('proportion')) {
    return { icon: PieChart, color: COLORS.chart.pink, label: 'Distribution' };
  }
  if (lower.includes('top') || lower.includes('highest') || lower.includes('best') || lower.includes('ranking')) {
    return { icon: TrendingUp, color: COLORS.chart.orange, label: 'Top' };
  }
  if (lower.includes('analyze') || lower.includes('insight') || lower.includes('pattern') || lower.includes('correlation')) {
    return { icon: Target, color: COLORS.chart.teal, label: 'Analyze' };
  }
  if (lower.includes('interactive') || lower.includes('drill down') || lower.includes('explore')) {
    return { icon: Zap, color: COLORS.chart.cyan, label: 'Interactive' };
  }
  // Default
  return { icon: Search, color: COLORS.primary, label: 'Explore' };
};

const FollowupSuggestions = ({ suggestions, onSuggestionClick, disabled = false }) => {
  if (!suggestions || !Array.isArray(suggestions) || suggestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 animate-fade-in-up mt-6">
      {/* Enhanced Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full border border-blue-200">
          <Sparkles className="w-5 h-5 animate-pulse-slow" style={{ color: COLORS.primary }} />
          <span className="text-sm font-bold text-gray-800">Explore Further</span>
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-blue-200 to-transparent"></div>
      </div>
      
      {/* Enhanced Suggestions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suggestions.map((suggestion, idx) => {
          const { icon: Icon, color, label } = getSuggestionIcon(suggestion);
          
          return (
            <button
              key={idx}
              disabled={disabled}
              className="group relative text-left p-5 bg-white hover:bg-gradient-to-br hover:from-blue-50 hover:to-indigo-50 border-2 border-gray-200 hover:border-blue-300 rounded-xl transition-all duration-300 text-sm card-hover btn-animate animate-scale-in shadow-sm hover:shadow-xl hover:-translate-y-1 disabled:opacity-50 disabled:pointer-events-none"
              style={{ animationDelay: `${idx * 0.05}s` }}
              onClick={() => onSuggestionClick(suggestion)}
            >
              {/* Icon Badge */}
              <div className="flex items-start gap-4 mb-3">
                <div 
                  className="p-3 rounded-xl shadow-sm group-hover:shadow-md transition-all duration-300 flex-shrink-0 group-hover:scale-110"
                  style={{ 
                    backgroundColor: `${color}15`,
                    border: `2px solid ${color}30`
                  }}
                >
                  <Icon 
                    className="w-5 h-5 transition-transform duration-200 group-hover:rotate-6" 
                    style={{ color: color }} 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  {/* Category Label */}
                  <div className="mb-2">
                    <span 
                      className="text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-md"
                      style={{ 
                        backgroundColor: `${color}15`,
                        color: color
                      }}
                    >
                      {label}
                    </span>
                  </div>
                  {/* Suggestion Text */}
                  <p className="font-semibold text-gray-900 leading-snug group-hover:text-blue-900 transition-colors text-base">
                    {suggestion}
                  </p>
                </div>
              </div>
              
              {/* Enhanced Hover Indicator */}
              <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-x-2 group-hover:translate-x-0">
                <div 
                  className="flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full shadow-md"
                  style={{ 
                    backgroundColor: color,
                    color: 'white'
                  }}
                >
                  <span>Ask Now</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
              
              {/* Decorative gradient overlay on hover */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5 transition-all duration-300 pointer-events-none"></div>
            </button>
          );
        })}
      </div>
      
      {/* Enhanced Footer */}
      <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Zap className="w-4 h-4" style={{ color: COLORS.chart.orange }} />
          <span className="font-medium">Click any suggestion to explore your data with interactive charts, graphs, and insights</span>
        </div>
      </div>
    </div>
  );
};

export default FollowupSuggestions;

